import type { Connection } from "@/lib/types";
import {
  authenticatedFastApiAxiosInstanceAllowEmptyString,
  publicFastApiAxiosInstance,
} from "@/lib/data/http";
import { type APIResponse } from "@/lib/types";

export type CreateMatterSignatureRequestV2Input = {
  matterId: string;
  fileId: string;
  fileIdDestination: string | null;
  expirationDate: string;
  contactConnections: Array<{
    contactId: string;
    label: string;
    signerOrder?: number;
  }>;
  userConnections: Array<{
    userId: string;
    label: string;
    signerOrder?: number;
  }>;
  fields: Array<{
    signerType: "contact" | "user";
    signerId: string;
    type:
      | "signature"
      | "initials"
      | "date"
      | "text"
      | "checkbox"
      | "attachment";
    label: string;
    required: boolean;
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
};

export type CreateMatterSignatureRequestV3Input =
  CreateMatterSignatureRequestV2Input & {
    notifications: {
      contactChannels: Array<"email" | "sms">;
      firmPhoneId?: string;
      notifyUsersByEmail: boolean;
    };
  };

type MatterSignatureRequestV2ConnectionContact = {
  id: string;
  title: string | null;
  individualFirstName: string | null;
  individualLastName: string | null;
  emails: Array<{ id: string; address: string | null }>;
  phones: Array<{ id: string; number: string | null }>;
};

export type MatterSignatureRequestV2Data = {
  id: string;
  matterId: string;
  fileId: string;
  expirationDate: string;
  contactConnections: Array<{
    contactId: string;
    signatureRequestPasscode: string;
    contact: MatterSignatureRequestV2ConnectionContact | null;
  }>;
  userConnections: Array<{
    userId: string;
    signatureRequestPasscode: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
  }>;
};

export const createMatterSignatureRequestV2 = (
  data: CreateMatterSignatureRequestV2Input,
) =>
  authenticatedFastApiAxiosInstanceAllowEmptyString
    .post<APIResponse<MatterSignatureRequestV2Data>>(
      "matters/v2/signature-requests",
      {
        ...data,
        // The V2 schema requires a string even when the destination is root.
        fileIdDestination: data.fileIdDestination ?? "",
      },
    )
    .then((res) => res.data);

export const createMatterSignatureRequestV3 = (
  data: CreateMatterSignatureRequestV3Input,
) =>
  authenticatedFastApiAxiosInstanceAllowEmptyString
    .post<
      APIResponse<{
        id: string;
        matterId: string;
        connections: Connection[];
        connectionsNextCursor?: string | null;
        notifications: { requested: number; succeeded: number; failed: number };
      }>
    >("matters/v4/signature-requests", {
      ...Object.fromEntries(
        Object.entries(data).filter(([key]) => key !== "contactConnections"),
      ),
      connections: data.contactConnections.map(
        ({ contactId, signerOrder }) => ({
          targetObjectId: contactId,
          targetObjectType: "contact",
          connectionType: "matter_signature_request_to_signer",
          signerOrder,
        }),
      ),
      fileIdDestination: data.fileIdDestination ?? "",
    })
    .then((res) => res.data);

export type MatterSignatureRequestPublicV2Data = {
  signatureRequest: {
    id: string;
    matterId: string;
    dateModified: string;
    name: string;
    presignedUrlKey: string;
    expirationDate: string;
    variables: unknown;
  };
  fields: Array<{
    id: string;
    signerType: "contact" | "user";
    signerId: string;
    type:
      | "signature"
      | "initials"
      | "date"
      | "text"
      | "checkbox"
      | "attachment";
    label: string;
    required: boolean;
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
    defaultValue: string | null;
    placeholder: string | null;
    validationMaxLength: number | null;
    validationPattern: string | null;
    fontFamily?: "Times New Roman" | "Arial" | "Courier New" | null;
    fontSize?: number | null;
  }>;
  connections: Array<
    Pick<
      Connection,
      | "id"
      | "editRevision"
      | "lifecycleRootId"
      | "dateModified"
      | "signatureRequestComplete"
      | "pending"
    >
  >;
  userConnections: Array<{
    dateModified?: string;
    label?: string;
    signatureRequestComplete?: boolean;
    pendingSigner?: boolean;
  }>;
  requestingContactConnection: {
    dateModified?: string;
    label?: string;
    signatureRequestComplete?: boolean;
    pendingSigner?: boolean;
  } | null;
  requestingUserConnection: {
    dateModified?: string;
    label?: string;
    signatureRequestComplete?: boolean;
    pendingSigner?: boolean;
  } | null;
};

export const retrieveMatterSignatureRequestPublicV2 = (data: {
  signatureRequestPasscode: string;
}) =>
  publicFastApiAxiosInstance
    .get<
      APIResponse<MatterSignatureRequestPublicV2Data>
    >(`matters/v4/signature-requests/${data.signatureRequestPasscode}`)
    .then((res) => res.data);

export const getMatterSignatureRequestPublicFileUrlV2 = (data: {
  clientMethod: "get_object" | "put_object";
  fileName?: string;
  key: string;
  signatureRequestPasscode: string;
}) =>
  publicFastApiAxiosInstance
    .post<
      APIResponse<{ key: string; presignedUrl: string }>
    >("matters/v2/signature-requests/files/pre-signed-url", data)
    .then((res) => res.data);

export type UpdateMatterSignatureRequestV2Input = {
  id: string;
  matterId: string;
  dateModified: string;
  expirationDate?: string;
  fileIdDestination?: string | null;
};

export const updateMatterSignatureRequestV2 = (
  data: UpdateMatterSignatureRequestV2Input,
) =>
  authenticatedFastApiAxiosInstanceAllowEmptyString
    .patch<
      APIResponse<MatterSignatureRequestV2Data>
    >("matters/v2/signature-requests", data)
    .then((res) => res.data);

export const deleteMatterSignatureRequestV2 = (data: {
  signatureRequestId: string;
}) =>
  authenticatedFastApiAxiosInstanceAllowEmptyString
    .delete<
      APIResponse<MatterSignatureRequestV2Data>
    >(`matters/v2/signature-requests/${data.signatureRequestId}`)
    .then((res) => res.data);

export const fulfillMatterSignatureRequestPublicV2 = (data: {
  signatureRequestPasscode: string;
  signatureRequestDateModified?: string;
  connectionDateModified?: string;
  signatureRequestFilePresignedUrlKey?: string;
  filePresignedUrlKey?: string;
  variableValues: Record<string, string>;
}) =>
  publicFastApiAxiosInstance
    .patch<
      APIResponse<Record<string, unknown>>
    >("matters/v2/signature-requests/fulfill", data)
    .then((res) => res.data);
